import { ReactNode } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import {
  Close,
  CheckBoxOutlined,
  CheckBoxOutlineBlankOutlined,
} from '@mui/icons-material'
import {
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Button,
  Skeleton,
  Chip,
} from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useGetOriginType } from '@/service-library/hooks/origin-types'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { DocumentChip } from '@/types/documents'
import { modHotKeyStr } from '@/utils/os'
import Drawer from '@/components/drawer/Drawer'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import AddressData from './AddressData'
import FieldInfoExtraData from './FieldInfoExtraData'
import FieldInfoLabeledValue from './FieldInfoLabeledValue'
import ListEntryData from './ListEntryData'
import { useDocumentChipsContext } from './providers/DocumentChipsProvider'
import { useDocumentsChangeSetsContext } from './providers/DocumentsChangeSetsProvider'
import { SelectedField } from './providers/SelectedFieldProvider'
import { DataLineageValues } from './useFieldValue'
import useGetValueData from './useGetValueData'

type FieldInfoDrawerProps = {
  overlay: OverlayState
  chips: DocumentChip[]
  selectedField: SelectedField | null
}

const dataLineage = {
  original_value: {
    name: 'Original Value',
    description:
      'This value comes from the combined values of any chips attached to the field.',
  },
  rule_value: {
    name: 'Rule Value',
    description:
      'This value is calculated by passing the original value through any rules applied to the field.',
  },
  manual_value: {
    name: 'Manual Value',
    description:
      'This value is only populated if you have manually changed the value.',
  },
  final_value: {
    name: 'Final Value',
    description:
      'This value is set based on the other three values. It will use the manual value first, but if it is not available, it will use the rule or original value. This is the value used when the document is exported.',
  },
  value: {
    name: 'Value',
  },
}

export default function FieldInfoDrawer({
  overlay,
  chips,
  selectedField,
}: FieldInfoDrawerProps) {
  const { document_row_id, field } = selectedField || {}
  const getValueData = useGetValueData()
  const {
    value,
    refId,
    jsonValue,
    dataLineageValues,
    metadata,
    origin_name,
    origin_type_id,
    ai_page_numbers,
  } = getValueData({
    fieldId: field?.id,
    rowId: document_row_id,
  })
  const { document, isLite } = useDocumentContext()
  // FieldInfoDrawer is rendered by SelectedFieldProvider, which is mounted
  // outside DocumentImagePageProvider. Using the shared
  // session-storage key keeps page navigation in sync without requiring that
  // provider in this component's tree.
  const [imagePage, setImagePage] = useSessionStorage(
    `${isLite ? 'lite' : 'validation'}-image-page-${document?.id}`,
    1,
  )

  const foundOnPages = [
    ...new Set([
      ...(ai_page_numbers || []),
      ...chips.map((chip) => chip.document_page_number),
    ]),
  ].sort((a, b) => a - b)

  const isSuperUser = useIsSuperUser()
  const { originType } = useGetOriginType({
    id: origin_type_id || '',
    staleTime: Infinity,
    enabled: overlay.isOpen,
  })

  const listLookupFieldRule = field?.project_grid_field_rules?.find(
    ({ rule }) => rule.code === 'list_lookup',
  )

  const jsonAddressValue =
    jsonValue?.address || jsonValue?.us_address ? jsonValue : null

  const dataValues =
    field?.project_grid_field_type.code === 'category'
      ? {
          value:
            field.category_items?.find(({ id }) => id === value)?.name || null,
        }
      : dataLineageValues

  const { clearFieldDocumentChips } = useDocumentChipsContext()
  const { isFieldUpdatingForIndicator } = useDocumentsChangeSetsContext()

  const fieldIsUpdating = field
    ? isFieldUpdatingForIndicator(field.id, document_row_id)
    : false

  useHotkeys(
    'mod+delete',
    () => {
      clearFieldDocumentChips(chips)
    },
    {
      enabled:
        field?.project_grid_field_type.code !== 'category' &&
        field?.project_grid_field_type.code !== 'picker' &&
        field?.input_behavior !== 'manual_only' &&
        !!chips.length,
      preventDefault: true,
      enableOnFormTags: true,
    },
    [chips, field?.project_grid_field_type.code, field?.input_behavior],
  )

  return (
    <Drawer overlay={overlay} anchor="right" size="sm">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        minHeight={52}
        onMouseDown={(e) => e.preventDefault()}
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography p={1.5} variant="h6" component="h2">
          <b>Field Details</b>
        </Typography>
        <IconButton
          onClick={() => overlay.close()}
          sx={{ height: 'fit-content' }}
        >
          <Close />
        </IconButton>
      </Stack>

      {selectedField ? (
        <>
          {foundOnPages.length > 0 && (
            <>
              <Stack
                spacing={1}
                p={1.5}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Typography
                  component="h3"
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    lineHeight: '12px',
                    width: 'fit-content',
                  }}
                  noWrap
                  color="text.secondary"
                >
                  Found on pages
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                >
                  {foundOnPages.map((pageNumber) => (
                    <Chip
                      label={pageNumber}
                      key={pageNumber}
                      color={imagePage === pageNumber ? 'primary' : 'neutral'}
                      onClick={() => setImagePage(() => pageNumber)}
                      size="small"
                    />
                  ))}
                </Stack>
              </Stack>
              <Divider variant="fullWidth" />
            </>
          )}
          <Stack spacing={1.5} p={1.5} onMouseDown={(e) => e.preventDefault()}>
            <FieldInfoLabeledValue
              label={{ name: 'Origin Name', description: '' }}
              value={origin_name}
            />
            <FieldInfoLabeledValue
              label={{ name: 'Origin Type', description: '' }}
              value={originType?.name}
            />
          </Stack>
          <Divider variant="fullWidth" />
          <Stack
            flexGrow={1}
            spacing={1.5}
            p={1.5}
            onMouseDown={(e) => e.preventDefault()}
          >
            {field?.project_grid_field_type.code !== 'category' &&
              field?.project_grid_field_type.code !== 'picker' &&
              field?.input_behavior !== 'manual_only' && (
                <Tooltip
                  title={`${modHotKeyStr} + Del`}
                  enterDelay={1000}
                  arrow
                >
                  {/* Add span to prevent errors regarding tooltip in disabled buttons */}
                  <span>
                    <Button
                      disabled={!chips.length}
                      onClick={() => clearFieldDocumentChips(chips)}
                      size="small"
                      startIcon={<Close />}
                      sx={{ width: 'fit-content' }}
                      variant="text"
                    >
                      <span>Clear All Chips</span>
                    </Button>
                  </span>
                </Tooltip>
              )}
            {Object.entries(dataValues).map(([key, value]) => {
              if (
                field?.input_behavior === 'manual_not_allowed' &&
                key === 'manual_value'
              )
                return
              let fieldValue: ReactNode = value as string
              if (fieldIsUpdating) {
                fieldValue = <Skeleton width="100%" />
              } else {
                if (field?.project_grid_field_type.code === 'checkbox') {
                  if (value === 'TrueBox' || value === 'True') {
                    fieldValue = (
                      <CheckBoxOutlined
                        fontSize="small"
                        sx={{ mt: '2px', ml: '-2px' }}
                      />
                    )
                  } else if (value === 'FalseBox' || value === 'False') {
                    fieldValue = (
                      <CheckBoxOutlineBlankOutlined
                        fontSize="small"
                        sx={{ mt: '2px', ml: '-2px' }}
                      />
                    )
                  }
                }
              }

              return (
                <FieldInfoLabeledValue
                  key={key}
                  label={dataLineage[key as keyof DataLineageValues | 'value']}
                  value={fieldValue}
                >
                  {isSuperUser &&
                    metadata?.confidence &&
                    key === 'original_value' && (
                      <Typography
                        color={SUPER_USER_ONLY_COLOR}
                        variant="caption"
                      >
                        Predicted Confidence:{' '}
                        {Math.floor(metadata.confidence * 100)}%
                      </Typography>
                    )}
                  {metadata?.weighted_confidence &&
                    key === 'original_value' && (
                      <Typography color="text.secondary" variant="caption">
                        Confidence:{' '}
                        {Math.floor(metadata.weighted_confidence * 100)}%
                      </Typography>
                    )}
                </FieldInfoLabeledValue>
              )
            })}
          </Stack>
          {jsonAddressValue && (
            <>
              <Divider variant="fullWidth" />
              <AddressData jsonValue={jsonAddressValue} />
            </>
          )}
          {field &&
            (field.project_grid_field_type.code === 'picker' ||
              listLookupFieldRule) &&
            refId && (
              <>
                <Divider variant="fullWidth" />
                <ListEntryData
                  enabled={overlay.isOpen}
                  field={field}
                  refId={refId}
                  listLookupFieldRule={listLookupFieldRule}
                />
              </>
            )}
          {metadata &&
            Object.entries(metadata).filter(([key, value]) => {
              return (
                !['confidence', 'weighted_confidence'].includes(key) &&
                Boolean(value)
              )
            }).length > 0 && (
              <>
                <Divider variant="fullWidth" />
                <FieldInfoExtraData metadata={metadata} />
              </>
            )}
        </>
      ) : (
        <Typography sx={{ p: 1.5 }}>No field selected.</Typography>
      )}
    </Drawer>
  )
}
